// ============================================================================
// Brief close-down — what still has to happen before a brief is put away
// ============================================================================
// Awarding a brief isn't the end of it. The supplier's final quote has to be on
// file, the final cost has to be checked by somebody rather than just typed in,
// and the post-event feedback has to be captured while anyone still remembers
// the job. Archiving is the point of no easy return — the brief leaves
// everyone's working view — so these are surfaced BEFORE it, not after.
//
// Every step is DERIVED from records that already exist. There is no checklist
// table, so there is nothing that can claim a step is done when the underlying
// record says otherwise ([[one-helper-dedup]]).
//
// Feedback deliberately isn't blocking: it depends on someone who may be on
// site, or on leave, and a brief shouldn't be un-archivable because a colleague
// hasn't answered four questions. The two money items ARE blocking-ish — they
// warn on archive — because they're near-impossible to reconstruct later.
// ============================================================================

export const CLOSEDOWN_STEPS = {
  FEEDBACK: 'feedback',
  FINAL_QUOTE: 'final_quote',
  FINAL_COST: 'final_cost',
  DEBRIEF: 'debrief',
};

// A brief is only in close-down once it's been awarded — nothing here applies
// to a brief that was cancelled, or closed with no winner.
export function isClosedown(brief) {
  return !!brief && brief.status === 'closed' && !!brief.awarded_supplier_id;
}

// The debrief step. Split out because it has four states rather than two, and
// burying that in the steps array made the array hard to read.
//
//   undecided        → open. Nobody has answered the question yet.
//   not required     → DONE. An explicit no is a complete answer.
//   required, none   → open. Answered yes, nothing started.
//   required, draft  → open. Started, not finished — a draft nobody publishes
//                      is the failure mode this step exists to catch.
//   required, published → DONE.
function debriefStep({ required, debrief } = {}) {
  const status = debrief?.status || null;
  const decided = required === true || required === false;
  const done = required === false || (required === true && status === 'published');

  const detail = !decided
    ? 'Decide whether this one needs a full write-up — not every brief does.'
    : required === false
      ? 'Not needed for this brief.'
      : !debrief
        ? 'Needed, but not started yet.'
        : status === 'published'
          ? 'Written up and published.'
          : 'Draft started — publish it to make the actions count.';

  return {
    key: CLOSEDOWN_STEPS.DEBRIEF,
    label: 'Full debrief required?',
    done,
    blocking: false,
    detail,
    // Rendering hints, so the surface doesn't re-derive the same four states.
    decided,
    required: decided ? required : null,
    debriefId: debrief?.id || null,
    debriefStatus: status,
  };
}

// The one shape every surface reads.
//   done      — is it satisfied
//   blocking  — should archiving warn about it
//   detail    — what to say underneath
export function deriveClosedown({ brief, feedback, debrief } = {}) {
  // The final quote already has a home on the brief (final_quote_file_url) with
  // its own upload — the checklist reads that rather than inventing a second
  // place for the same file.
  const hasQuote = !!brief?.final_quote_file_url;

  const feedbackDone = !!feedback && (feedback.status === 'complete' || !!feedback.completed_at);

  const amount = brief?.awarded_amount;
  const hasAmount = amount != null && amount !== '';
  // A confirmation belongs to the figure it was given for, so editing the
  // amount CLEARS it (done where the amount is saved, not inferred here from
  // timestamps that could disagree).
  const costDone = hasAmount && !!brief?.final_cost_confirmed_at;

  const steps = [
    {
      key: CLOSEDOWN_STEPS.FINAL_QUOTE,
      label: 'Final supplier quote on file',
      done: hasQuote,
      blocking: true,
      detail: hasQuote
        ? (brief.final_quote_file_name || 'Uploaded')
        : "Upload the quote the supplier actually invoiced against — it's the hardest thing to find later.",
      fileUrl: brief?.final_quote_file_url || null,
    },
    {
      key: CLOSEDOWN_STEPS.FINAL_COST,
      label: 'Final cost checked',
      done: costDone,
      blocking: true,
      // The FIGURE, once it is confirmed. The standalone "Final cost" block
      // that used to carry it has gone (outcome-restructure §1) — three facts
      // were being shown by seven pieces of interface — so the row it
      // duplicated has to hold the value itself.
      detail: !hasAmount
        ? 'No final cost recorded yet.'
        : costDone
          ? `£${Number(amount).toLocaleString('en-GB')} — confirmed against the final quote.`
          : 'Confirm the recorded amount matches what was actually agreed.',
      amount: hasAmount ? Number(amount) : null,
    },
    {
      key: CLOSEDOWN_STEPS.FEEDBACK,
      label: 'Post-event feedback recorded',
      done: feedbackDone,
      blocking: false,
      detail: feedbackDone
        ? 'Captured.'
        : 'Four questions about how the supplier did. Someone else can be asked to fill it in.',
    },
    // The one step that isn't purely derived, because "does this warrant a
    // write-up" is a judgement and nothing in the schema implies it — the
    // absence of a debrief means "not written", never "not needed". So the
    // DECISION is stored (briefs.debrief_required) and everything else about
    // this step is still derived: whether a debrief exists, and how far it got,
    // come from project_debriefs.
    //
    // Non-blocking, for the same reason as feedback: a debrief needs a
    // conversation with people who may be on site or on leave, and a brief
    // shouldn't be un-archivable while that's being arranged.
    debriefStep({ required: brief?.debrief_required, debrief }),
  ];

  const outstanding = steps.filter(s => !s.done);
  return {
    steps,
    outstanding,
    done: steps.filter(s => s.done).length,
    total: steps.length,
    complete: outstanding.length === 0,
    // What archiving should warn about — the things that can't be reconstructed.
    blockingOutstanding: outstanding.filter(s => s.blocking),
  };
}

// The sentence shown when someone archives with things outstanding. Names them
// rather than saying "some items are incomplete", so the decision to archive
// anyway is an informed one.
export function archiveWarning(closedown) {
  const names = (closedown?.blockingOutstanding || []).map(s => s.label.toLowerCase());
  if (names.length === 0) return null;
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Archiving hides this brief from everyone's working view, and ${list} ${names.length === 1 ? 'is' : 'are'} still outstanding. Archive anyway?`;
}
